/**
 * ============================================================
 * KEJJA OS — src/pages/finance/MpesaPage.tsx
 * Paste an M-Pesa SMS, preview the parsed transaction,
 * import it with one click.
 * ============================================================
 *
 * This is uniquely KEJJA. No global budget app does this.
 * Kenyan families live on M-Pesa SMS; pasting one should
 * feel magical.
 * ============================================================
 */

import { useState } from 'react';
import {
  Smartphone, Wand2, CheckCircle2, AlertCircle, ArrowDownCircle,
  ArrowUpCircle, RotateCcw,
} from 'lucide-react';

import { useFinanceContext } from './FinanceLayout';
import { useMpesaPreview, useMpesaImport, useFinanceAccounts } from '@/hooks/useFinance';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { formatMoney } from '@/lib/formatMoney';
import { cn } from '@/lib/utils';

const SAMPLE_SMS =
  'QJH4A2B3C Confirmed. Ksh 1,200.00 paid to NAIVAS SUPERMARKET. ' +
  'on 21/4/26 at 10:14 AM. New M-PESA balance is Ksh 4,320.00. ' +
  'Transaction cost, Ksh 0.00.';

export default function MpesaPage() {
  const { homeId, currencyCode } = useFinanceContext();
  const [raw, setRaw] = useState('');
  const [committed, setCommitted] = useState<boolean>(false);

  const previewMut = useMpesaPreview(homeId);
  const importMut = useMpesaImport(homeId);

  const { data: acctData } = useFinanceAccounts(homeId);
  const accounts = acctData?.accounts ?? [];
  const mpesaAccount = accounts.find((a) => a.accountType === 'mpesa');

  const preview = previewMut.data;
  const parsedOk = preview?.parsed && preview.preview;

  const handlePreview = () => {
    setCommitted(false);
    if (!raw.trim()) return;
    previewMut.mutate(raw.trim());
  };

  const handleImport = () => {
    importMut.mutate(
      { rawSms: raw.trim(), mpesaAccountId: mpesaAccount?.id ?? null },
      {
        onSuccess: () => {
          setCommitted(true);
        },
      },
    );
  };

  const handleReset = () => {
    setRaw(''); setCommitted(false);
    previewMut.reset(); importMut.reset();
  };

  return (
    <div className="space-y-5 max-w-2xl">
      {/* ============ HEADER ============ */}
      <div>
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-2">
          <Smartphone className="w-3.5 h-3.5" />
          M-Pesa SMS Import
        </div>
        <p className="text-sm text-muted-foreground">
          Paste an M-Pesa confirmation SMS below. KEJJA will parse the
          amount, merchant, date and reference — then you decide if it gets saved.
        </p>
      </div>

      {/* ============ INPUT ============ */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Paste the full SMS here
          </label>
          <textarea
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            placeholder="Ksh 1,200.00 paid to NAIVAS SUPERMARKET. on 21/4/26 at 10:14 AM…"
            className="w-full min-h-[120px] rounded-lg border border-border bg-background p-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-500/40 focus:border-orange-500"
          />
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={handlePreview}
              disabled={!raw.trim() || previewMut.isPending}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Wand2 className="w-4 h-4 mr-1.5" />
              {previewMut.isPending ? 'Parsing…' : 'Parse SMS'}
            </Button>
            <Button variant="outline" onClick={() => setRaw(SAMPLE_SMS)} disabled={!!raw.trim()}>
              Try sample
            </Button>
            {(raw || preview) && (
              <Button variant="ghost" onClick={handleReset}>
                <RotateCcw className="w-4 h-4 mr-1.5" />
                Reset
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* ============ PREVIEW ============ */}
      {preview && !parsedOk && (
        <Card className="border-rose-300 bg-rose-50/50 dark:bg-rose-950/10">
          <CardContent className="p-4 flex gap-3">
            <AlertCircle className="w-5 h-5 text-rose-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-sm text-foreground">Couldn't parse that one.</p>
              <p className="text-xs text-muted-foreground mt-1">
                {preview.error ?? 'Unknown M-Pesa SMS format. Try the sample or add it manually.'}
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {parsedOk && preview.preview && !committed && (
        <Card className="border-emerald-300 bg-gradient-to-br from-emerald-50 to-transparent dark:from-emerald-950/10">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
              <span className="text-xs font-semibold uppercase tracking-widest text-emerald-700 dark:text-emerald-300">
                Parsed successfully
              </span>
            </div>
            <PreviewRow
              direction={preview.preview.direction}
              amount={preview.preview.amount}
              currencyCode={preview.preview.currencyCode ?? currencyCode}
              merchant={preview.preview.merchant}
              reference={preview.preview.reference}
              occurredAt={preview.preview.occurredAt}
              description={preview.preview.description}
            />
            <div className="flex gap-2 mt-4 pt-4 border-t border-border">
              <Button
                onClick={handleImport}
                disabled={importMut.isPending}
                className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1"
              >
                {importMut.isPending ? 'Saving…' : 'Save to transactions'}
              </Button>
              <Button variant="outline" onClick={handleReset}>Discard</Button>
            </div>
            {!mpesaAccount && (
              <p className="text-[10px] text-muted-foreground mt-2">
                Tip: create an M-Pesa account under Accounts and future imports will attach to it automatically.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {committed && importMut.data?.transaction && (
        <Card className="border-emerald-400 bg-emerald-50 dark:bg-emerald-950/20">
          <CardContent className="p-4 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-sm text-foreground">Saved!</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Transaction is on your list. Paste another SMS or head to Transactions to review.
              </p>
            </div>
            <Button size="sm" variant="ghost" onClick={handleReset}>
              Another
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function PreviewRow({
  direction, amount, currencyCode, merchant, reference, occurredAt, description,
}: {
  direction: 'in' | 'out';
  amount: number;
  currencyCode: string;
  merchant: string | null;
  reference: string | null;
  occurredAt: string | null;
  description: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <div className={cn(
        'h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0',
        direction === 'in'
          ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300'
          : 'bg-orange-100 dark:bg-orange-950/40 text-orange-700 dark:text-orange-300',
      )}>
        {direction === 'in'
          ? <ArrowDownCircle className="w-6 h-6" />
          : <ArrowUpCircle className="w-6 h-6" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-foreground">
          {merchant ?? description}
        </div>
        <div className="flex items-center gap-2 mt-1 flex-wrap">
          <Badge variant="outline" className="text-[10px]">
            {direction === 'in' ? 'Received' : 'Paid'}
          </Badge>
          {reference && (
            <span className="text-[11px] font-mono text-muted-foreground">{reference}</span>
          )}
          {occurredAt && (
            <span className="text-[11px] text-muted-foreground">
              · {new Date(occurredAt).toLocaleString()}
            </span>
          )}
        </div>
      </div>
      <div className={cn(
        'text-xl font-bold tabular-nums flex-shrink-0',
        direction === 'in' ? 'text-emerald-600 dark:text-emerald-400' : 'text-foreground',
      )}>
        {direction === 'in' ? '+' : '−'}{formatMoney(amount, currencyCode)}
      </div>
    </div>
  );
}
